import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BrandedCard, BrandedInput, BrandedButton, BootPulse } from '../components';
import { SocialSignIn, providerName } from '../components/SocialSignIn';
import { useTenantContext } from '../hooks/useBranding';
import {
  kratosService,
  KratosFlow,
  KratosFlowError,
  mfaPath,
  nodeValue,
  verificationFlowOf,
} from '../services/kratosService';
import { fallbackAuthService } from '../services/fallbackAuthService';
import { hydraService, OAuthApiError, LoginRequest } from '../services/hydraService';
import {
  absoluteUrl,
  challengeIn,
  PendingApp,
  registrationPath,
  rememberApp,
  rememberedApp,
  sameOriginPath,
  signInPath,
  verificationPath,
} from '../services/signInFlow';
import './Login.css';

/** What the page before handed over (never in the URL): who just verified. */
interface Arrival {
  email?: string;
  verified?: boolean;
}

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { context, loading: contextLoading } = useTenantContext();
  const arrival = (location.state ?? {}) as Arrival;

  const [identifier, setIdentifier] = useState(arrival.email ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [kratosAvailable, setKratosAvailable] = useState(false);
  const [flow, setFlow] = useState<KratosFlow | null>(null);
  const flowId = flow?.id ?? '';
  // A refresh flow re-confirms an existing session (required before changing
  // credentials); the copy changes so it doesn't look like a lost session.
  const [isRefresh, setIsRefresh] = useState(false);

  // Hydra sends OAuth2 sign-ins here with ?login_challenge=. Once a Kratos
  // session exists, the control plane accepts the challenge as that
  // session's identity and returns Hydra's next hop (on to consent). After a
  // trip Kratos drove (social sign-in, account linking) the URL has only
  // ?flow=, and the challenge is in that flow's return_to.
  const [flowChallenge, setFlowChallenge] = useState<string | null>(null);
  const loginChallenge = searchParams.get('login_challenge') ?? flowChallenge;
  const [loginRequest, setLoginRequest] = useState<LoginRequest | null>(null);
  // The challenge outlived Hydra's ttl.login_consent_request (typically while
  // the person confirmed their email): sign them in to QAIM, then offer the
  // way back to the application. A ref too — finish() can run before a
  // re-render.
  const expired = useRef(false);
  const [challengeExpired, setChallengeExpired] = useState(false);
  const [appReturn, setAppReturn] = useState<PendingApp | null>(null);

  // Check if Kratos is enabled and available
  useEffect(() => {
    const start = async () => {
      // Kratos sends us back here with ?flow= for refresh/step-up, social
      // sign-in and account linking — adopt that flow rather than starting a
      // new one, which would drop the context that required it.
      const existingFlow = searchParams.get('flow');
      const refresh = searchParams.get('refresh') === 'true';
      const urlChallenge = searchParams.get('login_challenge');

      if (urlChallenge) {
        try {
          const request = await hydraService.getLoginRequest(urlChallenge);
          rememberApp(urlChallenge, request.client);
          setLoginRequest(request);
          // The application's "Create account" (prompt=registration/create):
          // open on sign-up — unless someone is signed in already, or the
          // visitor came back here to sign in (screen=login).
          if (
            request.screen === 'registration' &&
            !request.has_session &&
            !request.second_factor_required &&
            searchParams.get('screen') !== 'login' &&
            !existingFlow &&
            !refresh
          ) {
            navigate(registrationPath(urlChallenge), { replace: true });
            return;
          }
        } catch (err) {
          if (err instanceof OAuthApiError && err.code === 'login_challenge_expired') {
            expired.current = true;
            setChallengeExpired(true);
          }
          // Anything else surfaces when the challenge is completed.
        }
      }

      if (!context?.enabled_components.includes('kratos')) return;
      const available = await kratosService.isAvailable();
      setKratosAvailable(available);
      if (!available) return;

      try {
        const loaded = existingFlow
          ? // An expired flow (flows last 10 minutes) just means starting over.
            await kratosService
              .getLoginFlow(existingFlow)
              .catch(() => kratosService.initLoginFlow(undefined, refresh))
          : await kratosService.initLoginFlow(
              undefined,
              refresh,
              // So an emailed verification link, or Kratos after a social
              // sign-in, comes back to this sign-in.
              urlChallenge && !expired.current ? absoluteUrl(signInPath(urlChallenge)) : undefined
            );
        if (!urlChallenge) setFlowChallenge(challengeIn(loaded.return_to));
        // A social sign-in on an account with two-factor authentication:
        // Kratos wants the second factor before it issues the session.
        if (loaded.requested_aal === 'aal2' && !loaded.refresh) {
          navigate(mfaPath(sameOriginPath(loaded.return_to) ?? '/'), { replace: true });
          return;
        }
        setFlow(loaded);
        setIsRefresh(refresh || Boolean(loaded.refresh));
        // Linking a social account to an existing one: Kratos names the
        // account to sign in to.
        const linking = nodeValue(loaded, 'identifier');
        if (linking) setIdentifier(linking);
      } catch (err) {
        // A signed-in visitor gets a 400 here — that's a redirect, not an
        // outage. Only a genuine failure downgrades to the fallback path.
        if (
          err instanceof KratosFlowError &&
          err.kratosError?.error?.id === 'session_already_available'
        ) {
          void finish(searchParams.get('return_to') || '/', urlChallenge);
          return;
        }
        console.error('Failed to initialize Kratos flow:', err);
        setKratosAvailable(false);
      }
    };

    if (context) {
      void start();
    }
    // followReturnTo/searchParams are stable for the life of the page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]);

  // Resolve a usable Kratos flow at submit time. The mount-time probe above
  // is async, so a fast submit (autofill + Enter) could otherwise race it and
  // wrongly take the fallback path while Kratos is perfectly reachable.
  const ensureKratosFlow = async (): Promise<string | null> => {
    if (flowId) return flowId;
    if (!context?.enabled_components.includes('kratos')) return null;
    try {
      if (!(await kratosService.isAvailable())) return null;
      const started = await kratosService.initLoginFlow();
      setFlow(started);
      setKratosAvailable(true);
      return started.id;
    } catch (err) {
      if (
        err instanceof KratosFlowError &&
        err.kratosError?.error?.id === 'session_already_available'
      ) {
        throw err; // handled by the caller as "already signed in"
      }
      return null;
    }
  };

  // Follow return_to after login. Absolute URLs are honored only for other
  // local surfaces (e.g. the admin console on another localhost port) — a
  // foreign host would be an open redirect, so it falls back to the home page.
  const followReturnTo = (returnTo: string) => {
    if (/^https?:\/\//i.test(returnTo)) {
      try {
        const url = new URL(returnTo);
        const trusted =
          url.hostname === window.location.hostname ||
          url.hostname === 'localhost' ||
          url.hostname === '127.0.0.1';
        window.location.assign(trusted ? returnTo : '/');
      } catch {
        navigate('/');
      }
      return;
    }
    navigate(returnTo.startsWith('/') ? returnTo : '/');
  };

  const finish = async (returnTo: string, challenge: string | null = loginChallenge) => {
    if (!challenge) {
      followReturnTo(returnTo);
      return;
    }
    if (expired.current) {
      setAppReturn(rememberedApp(challenge));
      return;
    }
    try {
      const { redirect_to } = await hydraService.acceptLogin(challenge);
      window.location.assign(redirect_to);
    } catch (err) {
      if (err instanceof OAuthApiError && err.code === 'login_challenge_expired') {
        expired.current = true;
        setAppReturn(rememberedApp(challenge));
        return;
      }
      // Password given, second factor owed: take it, then come back here so
      // the pending OAuth2 challenge completes.
      if (err instanceof OAuthApiError && err.code === 'session_aal2_required') {
        navigate(mfaPath(signInPath(challenge)));
        return;
      }
      // The application asked for a fresh sign-in (prompt=login / max_age)
      // and this session is older: re-authenticate, then finish the same
      // challenge. A full load, so the page starts a refresh flow.
      if (
        err instanceof OAuthApiError &&
        err.code === 'session_refresh_required' &&
        searchParams.get('refresh') !== 'true'
      ) {
        window.location.assign(
          `/login?refresh=true&login_challenge=${encodeURIComponent(challenge)}`
        );
        return;
      }
      setError(err instanceof Error ? err.message : 'Could not complete the sign-in request.');
    }
  };

  // Social sign-in leaves the SPA: Kratos takes the browser to the provider
  // and back to the return_to given here — this page, with the application's
  // challenge (or a plain sign-in's return_to) and the provider, so a
  // first-time sign-up can say what to do next.
  const handleSocial = async (provider: string) => {
    setError('');
    setLoading(true);
    try {
      // A flow Kratos sent us here with (account linking, a failed attempt)
      // carries context — continue it. Otherwise start a fresh one.
      const returnTo = searchParams.get('return_to');
      const socialFlow =
        searchParams.get('flow') && flow
          ? flow
          : await kratosService.initLoginFlow(
              undefined,
              false,
              absoluteUrl(
                signInPath(loginChallenge, returnTo ? { return_to: returnTo, oidc: provider } : { oidc: provider })
              )
            );
      window.location.assign(await kratosService.startSocial('login', socialFlow.id, { provider }));
    } catch (err) {
      if (err instanceof KratosFlowError && err.kratosError?.error?.id === 'session_already_available') {
        await finish(searchParams.get('return_to') || '/');
        return;
      }
      setError(err instanceof Error ? err.message : 'Could not start signing in with that provider.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const returnTo = searchParams.get('return_to') || '/';

    try {
      const usableFlowId = await ensureKratosFlow();
      if (usableFlowId) {
        // Use Kratos login flow
        await kratosService.submitLogin(usableFlowId, identifier, password);
        await finish(returnTo);
      } else {
        // Use fallback authentication
        if (!context) {
          throw new Error('Tenant context not loaded');
        }

        let response;
        try {
          response = await fallbackAuthService.login({
            username: identifier,
            password,
            tenant_id: context.tenant_id,
          });
        } catch (fallbackErr) {
          // The control plane has no password login of its own; if we got here
          // the identity service is down. Say that, not "route not found".
          const message = fallbackErr instanceof Error ? fallbackErr.message : '';
          throw new Error(
            /not found/i.test(message)
              ? 'The sign-in service is unavailable right now. Please try again shortly.'
              : message || 'Login failed. Please try again.'
          );
        }

        // Store token (in production, use secure storage)
        sessionStorage.setItem('access_token', response.access_token);
        followReturnTo(returnTo);
      }
    } catch (err) {
      if (err instanceof KratosFlowError) {
        // Already signed in — just go where the visitor was headed.
        if (err.kratosError?.error?.id === 'session_already_available') {
          await finish(returnTo);
          return;
        }
        // Kratos signals a second-factor requirement as a structured error on
        // the flow rather than a generic failure — route to the MFA page
        // instead of showing "login failed".
        // Come back here after MFA so a pending OAuth2 challenge completes.
        const afterMfa = loginChallenge ? signInPath(loginChallenge) : returnTo;
        if (err.kratosError?.error?.id === 'session_aal2_required') {
          navigate(mfaPath(afterMfa));
          return;
        }
        // For an account with a second factor, Kratos answers a correct
        // password with 422 browser_location_change_required (pointing at
        // its second-factor flow) rather than an error. The password was
        // accepted: take the second factor on /mfa.
        if (err.kratosError?.error?.id === 'browser_location_change_required') {
          const { secondFactorRequired } = await kratosService.sessionState();
          if (secondFactorRequired) {
            navigate(mfaPath(afterMfa));
            return;
          }
          const target = err.kratosError.redirect_browser_to;
          if (typeof target === 'string' && target) {
            window.location.assign(target);
            return;
          }
        }
        // Sign-in requires a verified email (require_verified_address). Kratos
        // has just mailed a code for its own verification flow: enter it
        // there, then come back to this sign-in.
        if (err.kratosError?.error?.id === 'session_verified_address_required') {
          navigate(
            verificationPath(verificationFlowOf(err.kratosError.error.details?.continue_with), loginChallenge),
            { state: { email: identifier } }
          );
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (contextLoading) {
    return (
      <BrandedCard>
        <BootPulse label="Loading sign-in" />
      </BrandedCard>
    );
  }

  // Signed in, but the application's request expired on the way: the account
  // is fine, the application just has to be asked again.
  if (appReturn) {
    const app = appReturn.name || 'the application';
    return (
      <BrandedCard title="You're Signed In">
        <p className="login-notice" role="status">
          Your account is ready and you&apos;re signed in to QAIM. The sign-in request from {app}{' '}
          expired before it finished, so {app} needs to ask again — this time it goes straight
          through.
        </p>
        <div className="login-form">
          {appReturn.url ? (
            <BrandedButton
              variant="primary"
              fullWidth
              onClick={() => window.location.assign(appReturn.url as string)}
            >
              Return to {app}
            </BrandedButton>
          ) : (
            <BrandedButton variant="primary" fullWidth onClick={() => navigate('/')}>
              Continue
            </BrandedButton>
          )}
        </div>
      </BrandedCard>
    );
  }

  const appName = loginRequest?.client.client_name;
  const flowMessages = flow?.ui?.messages ?? [];
  // Back from a social sign-up with no session yet (sign-up never issues
  // one), or from confirming the email: say what finishes the sign-in. Only
  // once the flow is here — a visitor with a session is on the way out.
  const social = searchParams.get('oidc');
  const provider = social ? providerName(flow, social) : null;
  const arrivalNotice = arrival.verified
    ? `Your email address is confirmed. ${provider ? `Continue with ${provider}` : 'Sign in'} to finish.`
    : provider && flow && flowMessages.length === 0
      ? `Your account is ready. Continue with ${provider} to sign in.`
      : '';

  return (
    <BrandedCard title={isRefresh ? 'Confirm Your Password' : 'Sign In'}>
      {isRefresh && (
        <p className="login-info" role="status">
          For your security, confirm your password before changing account credentials.
        </p>
      )}
      {!isRefresh && appName && !challengeExpired && (
        <p className="login-info">Sign in to continue to {appName}.</p>
      )}
      {challengeExpired && (
        <p className="login-notice" role="status">
          This sign-in request has expired. Sign in here, then go back to the application.
        </p>
      )}
      {arrivalNotice && (
        <p className="login-notice" role="status">
          {arrivalNotice}
        </p>
      )}
      {searchParams.get('registered') === 'true' && (
        <p className="login-info" role="status">
          Account created. Enter the code we emailed you on the{' '}
          <a href="/verification" className="login-link">verification page</a>, then sign in.
        </p>
      )}
      {/* Kratos's own words: a failed social sign-in, or the email already
          belonging to an account (sign in to link the new provider). */}
      {flowMessages.map((message) => (
        <div
          key={message.id}
          className={message.type === 'error' ? 'login-error' : 'login-notice'}
          role={message.type === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </div>
      ))}
      <form onSubmit={handleSubmit} className="login-form">
        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}

        <BrandedInput
          type="email"
          name="identifier"
          label="Email"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          placeholder="Enter your email address"
          required
          autoComplete="username"
        />

        <BrandedInput
          type="password"
          name="password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          required
          autoComplete="current-password"
        />

        <BrandedButton
          type="submit"
          variant="primary"
          fullWidth
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </BrandedButton>
      </form>

      {!isRefresh && (
        <SocialSignIn
          flow={flow}
          allowed={loginRequest?.allowed_providers ?? null}
          onSelect={handleSocial}
          busy={loading}
        />
      )}

      <div className="login-links">
        <a href={registrationPath(loginChallenge)} className="login-link">
          Don't have an account? Sign up
        </a>
        <a href="/recovery" className="login-link">
          Forgot password?
        </a>
      </div>

      {!kratosAvailable && context?.enabled_components.includes('kratos') && (
        <div className="login-info">
          Using fallback authentication (Kratos unavailable)
        </div>
      )}
    </BrandedCard>
  );
};
